<template>
  <div ref="rootRef" class="usage-picker">
    <button
      type="button"
      class="add-trigger"
      :title="$t('project.general.sections.usage_definition.usage.tip.add')"
      :aria-expanded="isMenuOpen ? 'true' : 'false'"
      aria-haspopup="menu"
      :disabled="isMaxReached"
      @click="toggleMenu">
      <span class="add-trigger-icon">
        <IconPlus class="h-5 w-5" stroke="1.8" />
      </span>
      <span class="add-trigger-label">
        {{ $t('project.general.sections.usage_definition.usage.tip.add') }}
      </span>
      <IconChevronDown class="h-4 w-4 transition-transform" stroke="1.8" :class="{ 'rotate-180': isMenuOpen }" />
    </button>

    <div v-if="isMenuOpen" class="usage-menu" role="menu">
      <UsageHierarchyPicker
        :country="props.country"
        :usageCounts="usageCounts"
        :allowedUsageTypes="props.allowedUsageTypes"
        @select="onSelectUsageChoice" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, toRef, type PropType } from 'vue'
import { IconChevronDown, IconPlus } from '@tabler/icons-vue'
import { deepClone } from '@/helpers/objects_arrays'
import type { UsageItem, SharedUsageItem } from '@/schemas/general/types'
import { BuildingPlan, UsageType } from '@/schemas/general/enums'
import { useCalculatorForRole, type Role } from '@/stores/projectCenter/useCalculatorForRole'
import { DEFAULT_COMPLEXITY_PERCENTAGE } from '@arcexperts/core-types'
import * as Sentry from '@sentry/vue'
import UsageHierarchyPicker from '@/components/partials/inputs/UsageHierarchyPicker.vue'
import { STANDARD_USAGE_TYPES } from '@/helpers/usage-types'
const props = withDefaults(
  defineProps<{ country?: string | null; role?: Role; allowedUsageTypes?: readonly string[] }>(),
  {
    allowedUsageTypes: () => STANDARD_USAGE_TYPES,
  }
)
type UsageWithBuildingType = Omit<UsageItem | SharedUsageItem, 'type'> & {
  type: string
  buildingTypeId?: string | null
  buildingTypeName?: string | null
  requirementsLevel?: 'LOW' | 'STANDARD' | 'HIGH' | 'VERY_HIGH'
}
type UsagePickerSelection = {
  usageType: string
  buildingTypeId: string | null
  buildingTypeName: string | null
}

const setupCalculator = useCalculatorForRole(toRef(props, 'role'))
const minRatio = 5
const DEFAULT_REQUIREMENTS_LEVEL = 'STANDARD'

// Default shared usage item for General page (no corrections)
const defaultSharedUsageItem: SharedUsageItem = {
  type: UsageType.HOUSING,
  typeCounter: 0,
  ratio: 100,
  buildingPlan: BuildingPlan.NEW_BUILDING,
  rawConstruction: DEFAULT_COMPLEXITY_PERCENTAGE,
  facadeEnvelope: DEFAULT_COMPLEXITY_PERCENTAGE,
  interiors: DEFAULT_COMPLEXITY_PERCENTAGE,
  electricInstallation: DEFAULT_COMPLEXITY_PERCENTAGE,
  heatingVentilation: DEFAULT_COMPLEXITY_PERCENTAGE,
  sanitary: DEFAULT_COMPLEXITY_PERCENTAGE,
  requirementsLevel: DEFAULT_REQUIREMENTS_LEVEL,
} as SharedUsageItem & { requirementsLevel: string }

const modelValue = defineModel({ type: Array as PropType<UsageWithBuildingType[]> })
const isMenuOpen = ref(false)
const rootRef = ref<HTMLElement | null>(null)

const isMaxReached = computed(() => (modelValue.value?.length ?? 0) >= 8)
const usageCounts = computed<Record<string, number>>(() => {
  const counts: Record<string, number> = {}
  for (const usage of modelValue.value ?? []) {
    counts[usage.type] = (counts[usage.type] ?? 0) + 1
  }
  return counts
})

function toggleMenu(): void {
  if (isMaxReached.value) return
  isMenuOpen.value = !isMenuOpen.value
}

function closeMenu(): void {
  isMenuOpen.value = false
}

function onSelectUsageChoice(selection: UsagePickerSelection): void {
  addUsage(selection.usageType, selection.buildingTypeId, selection.buildingTypeName)
  closeMenu()
}

function onDocumentClick(event: MouseEvent): void {
  const target = event.target as Node | null
  if (!target || !rootRef.value?.contains(target)) {
    closeMenu()
  }
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick)
})

function addUsage(type: string, buildingTypeId?: string | null, buildingTypeName?: string | null): void {
  if (!modelValue.value) {
    return
  }
  if (modelValue.value?.length >= 8) {
    Sentry.logger.debug('Maximum usage items reached', { max: 8 })
    return
  }
  let tempUsages = deepClone(modelValue.value)

  // Prefer taking the new share from the item directly above the new entry (end of list),
  // and only continue upwards when that item cannot spare enough while staying >= minRatio.
  let remainingToAllocate = tempUsages.length > 0 ? minRatio : 0
  for (let i = tempUsages.length - 1; i >= 0 && remainingToAllocate > 0; i -= 1) {
    const spare = Math.max(0, tempUsages[i].ratio - minRatio)
    if (spare <= 0) continue

    const deduction = Math.min(spare, remainingToAllocate)
    tempUsages[i].ratio -= deduction
    remainingToAllocate -= deduction
  }

  // Safety fallback for invalid/legacy states: distribute any remainder from all items with spare ratio.
  if (remainingToAllocate > 0) {
    const donors = tempUsages
      .map((usage, index) => ({ index, spare: Math.max(0, usage.ratio - minRatio) }))
      .filter((entry) => entry.spare > 0)

    let remainder = remainingToAllocate
    while (remainder > 0 && donors.length > 0) {
      const eligible = donors.filter((entry) => tempUsages[entry.index].ratio > minRatio)
      if (!eligible.length) break

      const step = remainder / eligible.length
      let reducedThisPass = 0

      for (const entry of eligible) {
        const currentSpare = Math.max(0, tempUsages[entry.index].ratio - minRatio)
        if (currentSpare <= 0) continue
        const deduction = Math.min(currentSpare, step)
        tempUsages[entry.index].ratio -= deduction
        reducedThisPass += deduction
      }

      if (reducedThisPass <= 0) break
      remainder -= reducedThisPass
    }

    remainingToAllocate = Math.max(0, remainder)
  }

  const newUsageRatio = tempUsages.length > 0 ? minRatio - remainingToAllocate : 100
  // add new usage item - use calculator default if available, otherwise simple default
  const baseItem = (props.role ? setupCalculator.value.getDefaultUsageItem : defaultSharedUsageItem) as
    | UsageWithBuildingType
    | SharedUsageItem
  tempUsages.push({
    ...baseItem,
    type: type,
    ratio: newUsageRatio,
    requirementsLevel:
      ((baseItem as UsageWithBuildingType).requirementsLevel as string | undefined) ?? DEFAULT_REQUIREMENTS_LEVEL,
    buildingTypeId: buildingTypeId ?? null,
    buildingTypeName: buildingTypeName ?? null,
  } as UsageWithBuildingType)

  // Keep the total exactly at 100 (floating point correction).
  const totalRatio = tempUsages.reduce((sum, usage) => sum + usage.ratio, 0)
  const correction = 100 - totalRatio
  if (correction !== 0 && tempUsages.length > 0) {
    tempUsages[tempUsages.length - 1].ratio += correction
  }

  updateTypeCounter(tempUsages)
  modelValue.value = tempUsages
}

function updateTypeCounter(usageItems: UsageWithBuildingType[]): UsageWithBuildingType[] {
  const typeCount = new Map<string, number>()
  usageItems.forEach((usage) => {
    usage.typeCounter = typeCount.get(usage.type) || 0
    typeCount.set(usage.type, usage.typeCounter + 1)
  })
  return usageItems
}
</script>

<style scoped lang="postcss">
.usage-picker {
  @apply relative mx-px;
}

.add-trigger {
  @apply flex h-14 w-full items-center gap-3 border border-neutral-300 bg-white px-4;
  @apply transition-colors disabled:cursor-not-allowed disabled:opacity-60;
  span,
  svg {
    @apply text-neutral-750;
  }

  &:hover:not(:disabled) {
    @apply border-neutral-300;
    background-color: #f5f5f5;
  }
}

.add-trigger-icon {
  @apply flex h-8 w-8 items-center justify-center;
}

.add-trigger-label {
  @apply flex-grow text-left;
}

.usage-menu {
  @apply absolute z-[210] mt-2 border border-neutral-300 bg-white p-2;
  left: -10px;
  right: -10px;
  transform-origin: top center;
  animation: usage-menu-grow 100ms ease-out;
  box-shadow:
    0 4px 5px rgba(0, 0, 0, 0.13),
    0 10px 14px -6px rgba(0, 0, 0, 0.16);
}

@keyframes usage-menu-grow {
  from {
    opacity: 0;
    transform: scale(0.98);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
